import { UseInfiniteQueryResult } from '@tanstack/react-query';
import flatten from 'lodash.flatten';
import sortBy from 'lodash.sortby';
import uniqBy from 'lodash.uniqby';
import { useMemo, useState } from 'react';

import { TableState } from '@/components/common/table/types';

interface IProps<T> {
  query: UseInfiniteQueryResult<T[]>;
}

/**
 * This hook is written to work with 'useInfiniteQuery'.
 *
 * Context: Current backend API does not return pagination related data.
 * Therefore in frontend we have to determine the last page by checking length of the response.
 *
 * For example: if /batches?page=5 returns zero length array, we know 4th is the the last page.
 *
 */
export const useInfinitePagination = <T extends object>({ query }: IProps<T>) => {
  const [page, setPage] = useState(2);

  /**
   * useInfiniteQuery returns { data: { pages: [0: [], 1: [], ....] } }
   * therefore we have to flatten the data array into [0, 1, 2, .... n]
   */
  const data = useMemo(() => {
    return sortBy(uniqBy(flatten(query.data?.pages), 'id'), 'created_at');
  }, [query.dataUpdatedAt]);

  // check whether last element has any data in it to determine last page
  const isLastPage = useMemo(
    () => (query.data?.pages.at(-1)?.length === 0 ? true : false),
    [query.dataUpdatedAt],
  );

  // if NOT last page, fetch the next page's data
  const fetchNextPage = () => {
    if (!isLastPage) {
      query.fetchNextPage({ pageParam: page });
      setPage(page + 1);
    }
  };

  // this field is specifically for the <Table /> component's state.
  const state = useMemo<TableState>(
    () => (query.isFetching ? 'loading' : isLastPage ? 'no-data' : 'none'),
    [query.isFetching, isLastPage],
  );

  return { data, state, page, isLastPage, fetchNextPage };
};
